import {
  ActionBar,
  Card,
  Grid,
  GridColumn,
  GridRow,
  LazyLoader,
  Modal,
  Table,
  useDefaultSelecting,
} from '@bp/ui-components';
import { observer } from 'mobx-react-lite';
import { Suspense, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useConfirm } from '../../../hooks/useConfirm';
import { useIsMobile } from '../../../hooks/useIsMobile';
import { useMemorizedCacheTag } from '../../../hooks/useMemorizedCacheTag';
import { usePermissionChecker } from '../../../hooks/usePermissionChecker';
import { useSelectedSchoolYear } from '../../../hooks/useSelectedSchoolYear';
import { useDeleteDivisionMutation } from '../../../types/planung-graphql-client-defs';
import { showSuccessDeleteToast, showUserDbErrorToast } from '../../../utils/toast';
import { DivisionModal } from '../../Divisions/modals/DivisionModal';
import { DivisionsByClassTable } from '../../Divisions/tables/DivisionsByClassTable';
import { useClassesTable } from '../hooks/useClassesTable';
import { ClassesTableType } from '../types/ClassesTypes';

export const ClassDivisionTables = observer(() => {
  const { t } = useTranslation();
  const context = useMemorizedCacheTag('CLASS_DIVISIONS');

  const [, deleteDivisions] = useDeleteDivisionMutation();

  const permissions = usePermissionChecker();
  const isMobile = useIsMobile();
  const selectedSchoolYear = useSelectedSchoolYear();

  const initializedRef = useRef(false);

  const [search, setSearch] = useState<string>('');
  const [divisionUuid, setDivisionUuid] = useState<null | string>(null);
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  // mobil: Teilungen der angetippten Klasse im Modal statt in der rechten Spalte (mobile-plan.md E34)
  const [mobileDivisionsOpen, setMobileDivisionsOpen] = useState<boolean>(false);

  const [superiorClass, setSuperiorClass] = useState<{ uuid: string; name: string } | null>(null);
  const { rowSelection, onRowSelectionChange } = useDefaultSelecting({ 0: true });

  const { data, columns, tableSorting, tableSettings } = useClassesTable({
    context: 'divisions',
    columns: ['name', 'divisionsCount', 'color'],
  });

  const filteredData = useMemo(
    () =>
      data.filter((d) => {
        const lowercaseSearch = search.toLowerCase();
        const lowercaseName = d.name.toLowerCase();
        const divisionCount = Number(search);
        const lowercaseColor = d.color.colorLabel?.toLowerCase() ?? '';

        const name = lowercaseName.includes(lowercaseSearch);
        const divisions = !isNaN(divisionCount) ? divisionCount === d.divisionsCount : false;
        const color = lowercaseColor.includes(lowercaseSearch);

        return name || divisions || color;
      }),
    [data, search],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: bewusst nur beim Wechsel des Schuljahres zurücksetzen
  useEffect(() => {
    initializedRef.current = false;
    setSuperiorClass(null);
  }, [selectedSchoolYear?.uuid]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: onRowSelectionChange ist nicht stabil
  useEffect(() => {
    if (!initializedRef.current && filteredData.length > 0) {
      onRowSelectionChange({ 0: true });
      initializedRef.current = true;
      setSuperiorClass({ uuid: filteredData[0].uuid, name: filteredData[0].name });
    } else if (filteredData.length > 0) {
      onRowSelectionChange({ 0: true });
      setSuperiorClass({ uuid: filteredData[0].uuid, name: filteredData[0].name });
    } else {
      onRowSelectionChange({ 0: true });
    }
  }, [filteredData]);

  const { confirm, ConfirmationDialog } = useConfirm({
    defaultMessage: t('divisions.deleteMessage'),
  });

  function handleAdd() {
    setDivisionUuid(null);
    setModalOpen(true);
  }

  function handleClose() {
    setDivisionUuid(null);
    setModalOpen(false);
  }

  async function handleDelete(uuid: string) {
    const result = await confirm();

    if (!result) return;

    const { error } = await deleteDivisions({ uuid }, context);

    if (error) {
      showUserDbErrorToast();
    } else {
      showSuccessDeleteToast();
    }
  }

  const divisionsTable = superiorClass && (
    <DivisionsByClassTable
      classUuid={superiorClass.uuid}
      hideTitle={isMobile}
      readonly={!permissions?.canWriteMasterData()}
      onAdd={() => handleAdd()}
      onDelete={(uuid) => handleDelete(uuid)}
      onEdit={(uuid) => handleEdit(uuid)}
    />
  );

  function handleEdit(uuid: string) {
    setDivisionUuid(uuid);
    setModalOpen(true);
  }
  return (
    <>
      <Grid>
        <GridRow spacingBottom='none'>
          <ActionBar
            onGlobalFilterChange={(value) => {
              setSearch(value);
            }}
          />
        </GridRow>
        <GridRow spacingTop='none'>
          <GridColumn width={6}>
            <Table<ClassesTableType>
              data={filteredData}
              columns={columns}
              {...tableSettings}
              {...tableSorting}
              isDesktop={!isMobile}
              showActionBar={false}
              onRowClick={(_, row) => {
                setSuperiorClass({ uuid: row.original.uuid, name: row.original.name });
                onRowSelectionChange({ [row.index]: true });
                if (isMobile) setMobileDivisionsOpen(true);
              }}
              showRowHover
              showSelected
              selectOnClick
              rowMarker={rowSelection}
            />
          </GridColumn>
          {!isMobile && (
            <GridColumn width={6}>
              <Card contentPadding='none' style={{ paddingTop: 'var(--spacing-3)' }}>
                <Suspense fallback={<LazyLoader embedded forceHeight='15vh' />}>{divisionsTable}</Suspense>
              </Card>
            </GridColumn>
          )}
        </GridRow>
      </Grid>

      {isMobile && superiorClass && (
        <Modal
          title={`${t('divisions.title', { count: 2 })} ${superiorClass.name}`}
          isOpen={mobileDivisionsOpen}
          onRequestClose={() => setMobileDivisionsOpen(false)}
        >
          <Suspense fallback={<LazyLoader embedded forceHeight='15vh' />}>{divisionsTable}</Suspense>
        </Modal>
      )}

      {superiorClass && (
        <DivisionModal
          cacheTag='CLASS_DIVISIONS'
          divisionUuid={divisionUuid}
          isOpen={modalOpen}
          superiorClass={superiorClass}
          onClose={() => handleClose()}
        />
      )}

      <ConfirmationDialog />
    </>
  );
});
